import React, { useState, useEffect } from 'react';
import { 
  User, 
  MapPin, 
  Calendar, 
  Globe, 
  Heart, 
  BookOpen, 
  Edit, 
  Trash2, 
  Plus, 
  Save, 
  X,
  Loader2,
  Sparkles
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import RichTextEditor from '../components/RichTextEditor';

const ProfileManagement = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    bio: '',
    age: '',
    city: '',
    preferred_language: 'ar',
    life_philosophy: '',
    life_story: ''
  });
  const [interests, setInterests] = useState([]);
  const [editingInterest, setEditingInterest] = useState(null);
  const [newInterest, setNewInterest] = useState({
    title: '',
    description: '',
    icon: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const [profileResponse, interestsResponse] = await Promise.all([
        api.get('/profile/1/'),
        api.get('/interests/')
      ]);
      setProfile(profileResponse.data);
      setInterests(interestsResponse.data);
    } catch (error) {
      setProfile({
        name: '',
        bio: '',
        age: '',
        city: '',
        preferred_language: 'ar',
        life_philosophy: '',
        life_story: ''
      });
      setInterests([]);
      toast.error(t('Failed to load profile data'));
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSave = async () => {
    if (!profile.name.trim()) {
      toast.error(t('Please enter your name'));
      return;
    }
    setSaving(true);
    try {
      // Try to update first
      const response = await api.put(`/profile/1/`, profile);
      setProfile(response.data);
      toast.success(t('Profile updated successfully!'));
    } catch (error) {
      // If not found, create
      if (error.response && error.response.status === 404) {
        try {
          const response = await api.post(`/profile/`, profile);
          setProfile(response.data);
          toast.success(t('Profile created successfully!'));
        } catch (createError) {
          toast.error(t('Failed to create profile'));
        }
      } else {
        toast.error(t('Failed to update profile'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleInterestSave = async () => {
    if (!newInterest.title.trim()) {
      toast.error(t('Please enter a title for the interest'));
      return;
    }
    try {
      if (editingInterest) {
        const response = await api.put(`/interests/${editingInterest.id}/`, newInterest);
        setInterests(interests.map(interest => 
          interest.id === editingInterest.id ? response.data : interest
        ));
        toast.success(t('Interest updated successfully!'));
      } else {
        const response = await api.post('/interests/', newInterest);
        setInterests([...interests, response.data]);
        toast.success(t('Interest added successfully!'));
      }
      setNewInterest({ title: '', description: '', icon: '' });
      setEditingInterest(null);
    } catch (error) {
      toast.error(t('Failed to save interest'));
    }
  };

  const handleInterestDelete = async (interestId) => {
    if (!window.confirm(t('Are you sure you want to delete this interest?'))) {
      return;
    }
    try {
      await api.delete(`/interests/${interestId}/`);
      setInterests(interests.filter(interest => interest.id !== interestId));
      toast.success(t('Interest deleted successfully!'));
    } catch (error) {
      toast.error(t('Failed to delete interest'));
    }
  };

  const handleInterestEdit = (interest) => {
    setEditingInterest(interest);
    setNewInterest({
      title: interest.title,
      description: interest.description,
      icon: interest.icon
    });
  };

  const handleInterestCancel = () => {
    setEditingInterest(null);
    setNewInterest({ title: '', description: '', icon: '' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-violet-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-32 w-32 border-4 border-primary-200 border-t-primary-500"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-8 w-8 text-primary-400 animate-pulse" />
            </div>
          </div>
          <p className="mt-4 text-gray-600 font-medium">{t('Loading...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-violet-50 to-indigo-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4">
            <div className="p-3 bg-gradient-to-r from-primary-400 via-violet-400 to-primary-500 rounded-2xl shadow-lg">
              <User className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-600 via-violet-600 to-primary-700 bg-clip-text text-transparent">
                {t('Profile Management')}
              </h1>
              <p className="text-gray-600">{t('Manage your personal information and interests')}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Profile Information */}
          <div className="bg-white rounded-3xl shadow-xl border border-primary-100 p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">{t('Personal Information')}</h2>
              <button
                onClick={handleProfileSave}
                disabled={saving}
                className="flex items-center px-4 py-2 bg-gradient-to-r from-primary-500 to-violet-500 text-white rounded-xl hover:from-primary-600 hover:to-violet-600 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('Saving...')}
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {t('Save Profile')}
                  </>
                )}
              </button>
            </div>

            <div className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('Name')}</label>
                <div className="relative">
                  <input
                    type="text"
                    value={profile.name}
                    onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-3 pl-12 border border-primary-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                    placeholder={t('Your name')}
                  />
                  <User className="h-5 w-5 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
                </div>
              </div>

              {/* Age */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('Age')}</label>
                <div className="relative">
                  <input
                    type="text"
                    value={profile.age || ''}
                    onChange={(e) => setProfile(prev => ({ ...prev, age: e.target.value }))}
                    className="w-full px-4 py-3 pl-12 border border-primary-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                    placeholder={t('Your age')}
                  />
                  <Calendar className="h-5 w-5 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
                </div>
              </div>

              {/* City */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('City')}</label>
                <div className="relative">
                  <input
                    type="text"
                    value={profile.city}
                    onChange={(e) => setProfile(prev => ({ ...prev, city: e.target.value }))}
                    className="w-full px-4 py-3 pl-12 border border-primary-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                    placeholder={t('Your city')}
                  />
                  <MapPin className="h-5 w-5 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
                </div>
              </div>

              {/* Preferred Language */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('Preferred Language')}</label>
                <div className="relative">
                  <select
                    value={profile.preferred_language}
                    onChange={(e) => setProfile(prev => ({ ...prev, preferred_language: e.target.value }))}
                    className="w-full px-4 py-3 pl-12 border border-primary-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                  >
                    <option value="ar">{t('العربية')}</option>
                    <option value="en">{t('English')}</option>
                  </select>
                  <Globe className="h-5 w-5 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
                </div>
              </div>

              {/* Bio */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('Bio')}</label>
                <textarea
                  value={profile.bio}
                  onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 border border-primary-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 resize-none"
                  placeholder={t('Tell us about yourself...')}
                />
              </div>

              {/* Life Philosophy */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('Life Philosophy')}</label>
                <RichTextEditor
                  value={profile.life_philosophy}
                  onChange={(content) => setProfile(prev => ({ ...prev, life_philosophy: content }))}
                  placeholder={t('Share your life philosophy and beliefs...')}
                />
              </div>

              {/* Life Story */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">{t('Life Story')}</label>
                <RichTextEditor
                  value={profile.life_story}
                  onChange={(content) => setProfile(prev => ({ ...prev, life_story: content }))}
                  placeholder={t('Share your life story and experiences...')}
                />
              </div>
            </div>
          </div>

          {/* Interests Management */}
          <div className="bg-white rounded-3xl shadow-xl border border-primary-100 p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">{t('Interests')}</h2>
              <button
                onClick={() => setEditingInterest(null)}
                className="flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('Add Interest')}
              </button>
            </div>

            {/* Add/Edit Interest Form */}
            <div className="mb-6 p-4 bg-gradient-to-r from-primary-50 to-violet-50 rounded-2xl border border-primary-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {editingInterest ? t('Edit Interest') : t('Add New Interest')}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('Title')}</label>
                  <input
                    type="text"
                    value={newInterest.title}
                    onChange={(e) => setNewInterest(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-3 border border-primary-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                    placeholder={t('Interest title')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('Description')}</label>
                  <textarea
                    value={newInterest.description}
                    onChange={(e) => setNewInterest(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-3 border border-primary-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 resize-none"
                    placeholder={t('Describe your interest...')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('Icon (optional)')}</label>
                  <input
                    type="text"
                    value={newInterest.icon}
                    onChange={(e) => setNewInterest(prev => ({ ...prev, icon: e.target.value }))}
                    className="w-full px-4 py-3 border border-primary-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                    placeholder={t('e.g., heart, book, music')}
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={handleInterestSave}
                    className="flex items-center px-4 py-2 bg-gradient-to-r from-primary-500 to-violet-500 text-white rounded-xl hover:from-primary-600 hover:to-violet-600 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {editingInterest ? t('Update') : t('Add')}
                  </button>
                  {editingInterest && (
                    <button
                      onClick={handleInterestCancel}
                      className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors duration-200"
                    >
                      <X className="h-4 w-4 mr-2" />
                      {t('Cancel')}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Interests List */}
            <div className="space-y-4">
              {interests.map((interest) => (
                <div key={interest.id} className="p-4 border border-primary-200 rounded-2xl hover:bg-gradient-to-r hover:from-primary-50/50 hover:to-violet-50/50 transition-all duration-300">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        {interest.icon && (
                          <div className="p-2 bg-gradient-to-r from-primary-100 to-violet-100 rounded-xl">
                            <Heart className="h-4 w-4 text-primary-600" />
                          </div>
                        )}
                        <h4 className="text-lg font-semibold text-gray-900">{interest.title}</h4>
                      </div>
                      <p className="text-gray-600">{interest.description}</p>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleInterestEdit(interest)}
                        className="p-2 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-xl hover:from-blue-200 hover:to-indigo-200 transition-all duration-200"
                        title={t('Edit Interest')}
                      >
                        <Edit className="h-4 w-4 text-blue-600" />
                      </button>
                      <button
                        onClick={() => handleInterestDelete(interest.id)}
                        className="p-2 bg-gradient-to-r from-red-100 to-primary-100 rounded-xl hover:from-red-200 hover:to-primary-200 transition-all duration-200"
                        title={t('Delete Interest')}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              
              {interests.length === 0 && (
                <div className="text-center py-8">
                  <div className="p-4 bg-gradient-to-r from-primary-100 to-violet-100 rounded-3xl inline-block mb-4">
                    <Heart className="h-8 w-8 text-primary-500" />
                  </div>
                  <p className="text-gray-600">{t('No interests added yet. Add your first interest above!')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileManagement; 